<template>
  <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="width: 900px; max-width: 90vw;">
      <q-card-section>
        <div class="text-h6">
          <q-icon name="location_searching" color="warning" class="q-mr-sm" />
          Template In Use
        </div>
      </q-card-section>

      <q-card-section>
        <div class="text-body1 q-mb-md">
          <q-icon name="warning" color="warning" class="q-mr-sm" />
          The template "{{ templateName }}" cannot be deleted because it is currently being used.
        </div>

        <div class="row items-center q-mb-md">
          <q-chip color="warning" text-color="white">
            {{ locations.length }} {{ locations.length === 1 ? 'Location' : 'Locations' }}
          </q-chip>
          <q-space />
          <q-btn
            v-if="locations.length > 0"
            color="negative"
            icon="delete_sweep"
            label="Delete All"
            outline
            size="sm"
            :loading="deletingAll"
            @click="$emit('delete-all')"
          />
        </div>

        <q-table
          :rows="locations"
          :columns="columns"
          row-key="id"
          flat
          bordered
          dense
          :pagination="{ rowsPerPage: 10 }"
          :loading="deletingItem"
        >
          <template #body-cell-name="props">
            <q-td :props="props">
              <div class="text-weight-medium">{{ props.row.name }}</div>
              <div v-if="props.row.equipment_name" class="text-caption text-grey">
                Equipment: {{ props.row.equipment_name }}
              </div>
            </q-td>
          </template>
          <template #body-cell-location="props">
            <q-td :props="props">
              <div v-if="props.row.site" class="text-caption">
                <q-icon name="business" size="xs" class="q-mr-xs" />
                {{ props.row.site }}
              </div>
              <div v-if="props.row.building" class="text-caption">
                <q-icon name="domain" size="xs" class="q-mr-xs" />
                {{ props.row.building }}
                <span v-if="props.row.level"> - {{ props.row.level }}</span>
              </div>
              <div v-if="props.row.space" class="text-caption">
                <q-icon name="room" size="xs" class="q-mr-xs" />
                {{ props.row.space }}
              </div>
            </q-td>
          </template>
          <template #body-cell-actions="props">
            <q-td :props="props" class="text-center">
              <q-btn
                flat
                round
                dense
                icon="delete"
                color="negative"
                size="sm"
                :loading="props.row.deleting"
                @click="$emit('delete-item', props.row)"
              >
                <q-tooltip>Delete this {{ props.row.type }}</q-tooltip>
              </q-btn>
            </q-td>
          </template>
        </q-table>

        <q-banner class="bg-blue-1 q-mt-md" rounded>
          <template #avatar>
            <q-icon name="info" color="info" />
          </template>
          <div v-if="locations.length > 0">
            You can:
            <ol class="q-mt-sm q-mb-none">
              <li>Delete individual items using the delete button for each row</li>
              <li>Delete all items at once using the "Delete All" button</li>
              <li>Once all items are removed, the template can be deleted</li>
            </ol>
          </div>
          <div v-else class="text-positive">
            <q-icon name="check_circle" color="positive" class="q-mr-xs" />
            All items have been removed. You can now delete the template.
          </div>
        </q-banner>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          v-if="locations.length === 0"
          flat
          label="Delete Template Now"
          color="negative"
          icon="delete"
          :loading="deletingTemplate"
          @click="$emit('retry-delete-template')"
        />
        <q-btn flat label="Close" color="primary" @click="$emit('close')" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { defineProps, defineEmits } from 'vue';

  const props = defineProps({
    modelValue: { type: Boolean, required: true },
    templateName: { type: String, required: true },
    locations: { type: Array, required: true },
    columns: { type: Array, required: true },
    deletingItem: { type: Boolean, default: false },
    deletingAll: { type: Boolean, default: false },
    deletingTemplate: { type: Boolean, default: false },
  });

  const emit = defineEmits([
    'update:modelValue',
    'delete-item',
    'delete-all',
    'retry-delete-template',
    'close',
  ]);
</script>
